<template>
	<BaseDrawer
		:show.sync="isShow"
		title="Insert JSON-LD"
		size="xl"
		data-test-id="drawer-json-ld-template"
		@close="close"
	>
		<template v-if="isShow">
			<CRow>
				<CCol lg="12" class="mb-4">
					<p class="typo-body-2 color-black-45 mb-0">
						บล็อกนี้จะไม่แสดงบนหน้าเว็บ — storefront จะดึงออกไปใส่ใน
						<code>&lt;head&gt;</code> เป็น JSON-LD ให้เอง
						· <code>@context</code> ระบบเติมให้อยู่แล้ว ใส่มาก็ได้ ไม่ใส่ก็ได้
					</p>
				</CCol>
				<CCol lg="12" class="mb-3">
					<BaseDropDown
						v-model="selectedTemplate"
						:options="JSON_LD_TEMPLATE_OPTIONS"
						:searchable="false"
						:allow-empty="false"
						label="name"
						track-by="value"
						label-drop-down="เลือกรูปแบบ"
						class="flex-fill"
						@input="handleSelectTemplate"
					/>
				</CCol>
				<CCol lg="12">
					<CTextarea
						v-model="json"
						label="JSON-LD"
						rows="18"
						class="json-ld-textarea"
						:is-valid="isValid"
						:invalid-feedback="errorMessage"
						placeholder="วางหรือพิมพ์ JSON-LD ที่นี่"
					/>
				</CCol>
				<CCol lg="12">
					<ul class="typo-caption color-black-45 pl-3 mb-0">
						<li>
							ห้ามใช้ <code>@type</code> เหล่านี้ เพราะหน้าปล่อยให้อยู่แล้วและจะชนกัน:
							{{ blockedTypeText }}
						</li>
						<li>
							ถ้าข้อความข้างในมีคำว่า <code>&lt;/script&gt;</code>
							ต้องเขียนเป็น <code>&lt;\/script&gt;</code>
						</li>
						<li>ใส่ได้หลายบล็อกในหน้าเดียว · รองรับทั้ง object เดี่ยว, array และ <code>@graph</code></li>
					</ul>
				</CCol>
			</CRow>
		</template>
		<template #footer>
			<BaseModalFooter
				:disabled-confirm="!isValid || !json"
				confirm-button-text="Insert"
				@onCancel="close"
				@onConfirm="handleConfirm"
			/>
		</template>
	</BaseDrawer>
</template>

<script>
import BaseDrawer from '@/components/BaseDrawer.vue';
import BaseDropDown from '@/components/BaseDropDown.vue';
import BaseModalFooter from '@/components/BaseModalFooter.vue';
import {
	JSON_LD_BLOCKED_TYPES,
	JSON_LD_TEMPLATE,
	JSON_LD_TEMPLATE_OPTIONS,
	getJsonLdTemplateJson,
} from '../enums/jsonLd';

/**
 * Drawer เลือก template ของ JSON-LD แล้วแทรกลงคอนเทนต์
 *
 * เปิดจากปุ่มใน toolbar ของ `BaseRichTextEditor` แบบ imperative (`$refs[...].open()`)
 * ตามแพตเทิร์น modal/drawer ของโปรเจกต์ · emit `onConfirm` เป็น **JSON string ที่ validate แล้ว**
 * ให้ตัว editor เอาไปห่อ `<script type="application/ld+json">` เอง
 *
 * ตรวจให้ตั้งแต่ตอนกรอกเพื่อไม่ให้ต้องไปเจอ error ตอนกด Save ของฟอร์มใหญ่
 * (validator `jsonLdScriptOnly` ยังทำงานอยู่เป็นชั้นสุดท้ายเสมอ)
 */
export default {
	name: 'DrawerJsonLdTemplate',
	components: {
		BaseDrawer,
		BaseDropDown,
		BaseModalFooter,
	},
	data() {
		return {
			JSON_LD_TEMPLATE_OPTIONS,
			isShow: false,
			json: '',
			selectedTemplate: null,
		};
	},
	computed: {
		blockedTypeText() {
			return JSON_LD_BLOCKED_TYPES.join(', ');
		},

		/**
		 * ผลตรวจของ JSON ที่กรอก — คืน `null` เมื่อผ่าน
		 *
		 * @returns {String|null}
		 */
		errorMessage() {
			if (!this.json) {
				return null;
			}

			let parsed;

			try {
				parsed = JSON.parse(this.json);
			} catch (error) {
				return `JSON ไม่ถูกต้อง: ${error.message}`;
			}

			const nodes = [].concat(parsed?.['@graph'] ?? parsed ?? []);
			const blocked = JSON_LD_BLOCKED_TYPES.map((type) => type.toLowerCase());
			const usedBlockedTypes = nodes.reduce((result, node) => {
				const types = [].concat(node?.['@type'] ?? []);
				const hit = types.filter((type) => blocked.includes(String(type).toLowerCase()));

				return result.concat(hit);
			}, []);

			if (usedBlockedTypes.length) {
				return `ใช้ @type ที่ห้ามไว้: ${usedBlockedTypes.join(', ')} — storefront จะตัดบล็อกนี้ทิ้ง`;
			}

			if (!nodes.some((node) => node?.['@type'])) {
				return 'ทุก node ต้องมี @type ไม่งั้น storefront จะข้ามไป';
			}

			return null;
		},
		isValid() {
			return this.errorMessage === null;
		},
	},
	methods: {
		open() {
			this.isShow = true;
			this.selectedTemplate = JSON_LD_TEMPLATE_OPTIONS[0];
			this.json = getJsonLdTemplateJson(JSON_LD_TEMPLATE_OPTIONS[0].value);
		},
		close() {
			this.isShow = false;
			this.json = '';
			this.selectedTemplate = null;
		},

		/**
		 * เปลี่ยน template แล้วเขียนทับช่อง JSON
		 *
		 * ทับได้เลยโดยไม่ต้องถาม เพราะยังไม่ได้แทรกลงคอนเทนต์ กด Cancel ก็ไม่มีอะไรเสีย
		 * · free form ตั้งใจล้างให้ว่างเพื่อให้เริ่มเขียนเองจากศูนย์
		 */
		handleSelectTemplate(option) {
			this.json = option?.value === JSON_LD_TEMPLATE.FREE_FORM
				? ''
				: getJsonLdTemplateJson(option?.value);
		},
		handleConfirm() {
			if (!this.isValid || !this.json) {
				return;
			}

			this.$emit('onConfirm', this.json);
			this.close();
		},
	},
};
</script>

<style lang="scss" scoped>
	.json-ld-textarea ::v-deep textarea {
		font-family: monospace;
		white-space: pre;
	}
</style>
